/**
 * @fileoverview Keeps the global store usable when it was created by a
 * different Firework binary.
 *
 * `_fwn.globalStore` is shared by every Firework script on the page, and by the
 * parent window for PIP iframes. Whichever binary calls `getGlobalStore` first
 * creates the store and freezes its slice shape for all the others, so a binary
 * that adds a state key or an action reads `undefined` for it whenever an older
 * binary won that race.
 *
 * Backfilling is additive only: we add what this binary expects and is missing,
 * and never overwrite what is already there. That keeps the pass correct in
 * both directions - an older binary adopting a newer store adds nothing
 * harmful - and makes it idempotent, so the store converges on the union of the
 * shapes of every binary on the page.
 */
import { logError } from '@core/logging'
import { withLenses } from '@core/zustand/lens'

import type { IGlobalStore } from './globalStore'
import type { IGlobalState } from './state'
import { createState } from './state'

/**
 * Realm-safe plain-object check.
 *
 * `instanceof Object` and a `proto === Object.prototype` comparison both fail
 * for values created in the parent window, which is exactly the case this
 * module exists for. Walking to the end of the prototype chain does not, and it
 * still excludes arrays, `Set`s and class instances, whose prototypes sit one
 * link further down.
 */
function isPlainObject(value: unknown): value is Record<string, unknown> {
  if (typeof value !== 'object' || value === null) {
    return false
  }
  const proto = Object.getPrototypeOf(value)
  return proto === null || Object.getPrototypeOf(proto) === null
}

/**
 * Adds every key `template` defines and `target` is missing, recursing into
 * plain objects only. Existing values are never overwritten, so data, action
 * closures and `pageLoadId` all survive untouched. A key that is present but
 * `undefined` counts as missing only when the template has a real value for it:
 * it crashes a selector just the same, whereas a key both sides leave undefined
 * - `liveStream.heartbeatInterval`, `player.isUserMuted` - is not skew at all
 * and must not be reported as any.
 *
 * Branches that gained nothing are returned by reference so that untouched
 * slices keep their identity and do not re-render their subscribers.
 * @param target
 * @param template
 * @param path
 */
export function backfillMissing(
  target: unknown,
  template: unknown,
  path = '',
): { added: string[]; value: unknown } {
  if (!isPlainObject(target) || !isPlainObject(template)) {
    return { added: [], value: target }
  }

  const added: string[] = []
  let next = target

  Object.keys(template).forEach((key) => {
    const keyPath = path ? `${path}.${key}` : key

    const isMissing =
      !(key in target) ||
      (target[key] === undefined && template[key] !== undefined)

    if (isMissing) {
      if (next === target) {
        next = { ...target }
      }
      next[key] = template[key]
      added.push(keyPath)
      return
    }

    const child = backfillMissing(target[key], template[key], keyPath)
    if (child.added.length) {
      if (next === target) {
        next = { ...target }
      }
      next[key] = child.value
      added.push(...child.added)
    }
  })

  return { added, value: next }
}

/**
 * Fills in the slices, state keys and actions this binary expects but the
 * adopted store does not have. Call it only for a store created by another
 * binary - a store we created ourselves is already the right shape.
 * @param store
 */
export function reconcileStoreShape(store: IGlobalStore): void {
  try {
    // Built against the adopted store, so the actions it carries are already
    // bound to that store's `setState` and are safe to graft onto it.
    const template = withLenses<IGlobalState>(createState)(
      store.setState,
      store.getState,
      store,
    )

    const { added, value } = backfillMissing(store.getState(), template)

    if (!added.length) {
      return
    }

    store.setState(value as IGlobalState, false)
  } catch (err) {
    // A store we cannot reconcile is still a store the widgets can read, so
    // report and carry on rather than taking the page down.
    logError(err instanceof Error ? err : new Error(`${err}`))
  }
}
